import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/http";
import { toast } from "sonner";
import { Trophy, Gift, Users, Flag, Award, Check, Lock } from "lucide-react";

const Progress = ({ value, max }) => (
  <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${Math.min(100, max ? (value / max) * 100 : 0)}%` }} /></div>
);

export const ClientRewards = ({ clientId, isAdmin, onChanged }) => {
  const [r, setR] = useState(null);
  const [adj, setAdj] = useState({ points: "", reason: "" });
  const load = () => api.get(`/clients/${clientId}/rewards`).then(({ data }) => setR(data)).catch(() => setR({ enabled: false }));
  useEffect(() => { load(); }, [clientId]);
  if (!r) return <div className="text-sm text-muted-foreground py-4">Loading…</div>;
  if (!r.enabled) return <div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground" data-testid="rewards-disabled">Rewards are switched off. Turn them on in Setup → Grow → Rewards & referrals.</div>;

  const redeem = async (rw) => {
    try { const { data } = await api.post(`/clients/${clientId}/rewards/redeem`, { reward_id: rw.id }); toast.success(`Redeemed ${rw.name}${data.voucher_code ? ` — voucher ${data.voucher_code}` : ""}`); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't redeem"); }
  };
  const markUsed = async (id) => { try { await api.post(`/clients/${clientId}/rewards/redemptions/${id}/use`); toast.success("Marked as used"); load(); } catch { toast.error("Couldn't update"); } };
  const adjust = async () => {
    const p = parseInt(adj.points); if (!p) return toast.error("Enter points (+/-)");
    try { await api.post(`/clients/${clientId}/rewards/adjust`, { points: p, reason: adj.reason }); setAdj({ points: "", reason: "" }); toast.success("Points adjusted"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't adjust"); }
  };
  const nxt = r.next_reward;

  return (
    <div className="space-y-4" data-testid="client-rewards">
      <div className="rounded-2xl border bg-primary/5 p-4 flex flex-wrap items-center gap-4" data-testid="rewards-balance-card">
        <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center"><Trophy className="h-6 w-6 text-primary" /></div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{r.program_name}</div>
          <div className="font-heading font-bold text-2xl tabular-nums" data-testid="rewards-balance">{r.balance} <span className="text-sm font-normal text-muted-foreground">{r.points_name}</span></div>
          {nxt && <div className="mt-1.5 space-y-1"><Progress value={r.balance} max={nxt.points_cost} /><div className="text-xs text-muted-foreground">{r.balance >= nxt.points_cost ? `Can redeem ${nxt.name}` : `${nxt.points_cost - r.balance} ${r.points_name} to ${nxt.name}`}</div></div>}
        </div>
        <div className="text-xs text-muted-foreground text-right">{r.visits} paid visits{r.referred_by ? <><br />Referred by <b>{r.referred_by.name}</b></> : null}</div>
      </div>

      <div>
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" />Badges</div>
        <div className="flex flex-wrap gap-1.5" data-testid="rewards-badges">
          {r.badges.map((b) => (
            <span key={b.key} title={`${b.progress}/${b.target}`} className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs border ${b.earned ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-dashed"}`} data-testid={`badge-${b.key}`}>{b.earned ? <Check className="h-3 w-3" /> : <Lock className="h-3 w-3" />}{b.label}</span>
          ))}
        </div>
      </div>

      {r.rewards.length > 0 && (
        <div>
          <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5"><Gift className="h-3.5 w-3.5" />Rewards</div>
          <div className="space-y-1.5">
            {r.rewards.map((rw) => (
              <div key={rw.id} className="flex items-center gap-3 rounded-lg border p-2.5 text-sm" data-testid={`reward-${rw.id}`}>
                <div className="flex-1"><div className="font-medium">{rw.name}</div><div className="text-xs text-muted-foreground">{rw.points_cost} {r.points_name}{rw.auto ? " · auto" : ""}</div></div>
                <Button size="sm" variant={r.balance >= rw.points_cost ? "default" : "outline"} disabled={r.balance < rw.points_cost} onClick={() => redeem(rw)} className="rounded-lg h-8" data-testid={`redeem-${rw.id}`}>Redeem</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {r.challenges.length > 0 && (
        <div>
          <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5"><Flag className="h-3.5 w-3.5" />Challenges</div>
          <div className="space-y-1.5">
            {r.challenges.map((c) => (
              <div key={c.id} className="rounded-lg border p-2.5 text-sm space-y-1" data-testid={`challenge-${c.id}`}>
                <div className="flex justify-between"><span className="font-medium">{c.name}</span><span className={`text-xs ${c.done ? "text-primary font-semibold" : "text-muted-foreground"}`}>{c.done ? "Complete" : `${c.progress} / ${c.target}${c.end ? ` · ends ${c.end}` : ""}`}</span></div>
                <Progress value={c.progress} max={c.target} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Referrals ({r.referrals.length})</div>
        {r.referrals.length === 0 ? <p className="text-xs text-muted-foreground">No referred friends yet. Pick this client as the referrer when adding a new client.</p> : (
          <div className="space-y-1">{r.referrals.map((f) => <div key={f.id} className="flex justify-between text-sm rounded-lg border px-2.5 py-1.5" data-testid={`referral-${f.id}`}><span>{f.name}</span><span className={`text-xs ${f.referral_rewarded ? "text-primary" : "text-muted-foreground"}`}>{f.referral_rewarded ? "First visit done ✓" : "Awaiting first paid visit"}</span></div>)}</div>
        )}
      </div>

      {r.redemptions.length > 0 && (
        <div>
          <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5">Rewards given</div>
          <div className="space-y-1">{r.redemptions.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border px-2.5 py-1.5" data-testid={`redemption-${d.id}`}>
              <span>{d.reward_name}{d.voucher_code ? <span className="text-xs text-muted-foreground"> · {d.voucher_code}</span> : null}<span className="text-xs text-muted-foreground"> · {(d.created_at || "").slice(0, 10)}</span></span>
              {d.status === "available" ? <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => markUsed(d.id)} data-testid={`use-${d.id}`}>Mark used</Button> : <span className="text-xs text-muted-foreground capitalize">{d.status}</span>}
            </div>))}</div>
        </div>
      )}

      <div>
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-1.5">Points history</div>
        {r.ledger.length === 0 ? <p className="text-xs text-muted-foreground">Nothing earned yet.</p> : (
          <div className="divide-y rounded-lg border max-h-64 overflow-y-auto" data-testid="rewards-ledger">
            {r.ledger.map((l) => <div key={l.id} className="flex justify-between gap-2 px-2.5 py-1.5 text-sm"><span className="truncate">{l.reason}<span className="text-xs text-muted-foreground"> · {(l.created_at || "").slice(0, 10)}</span></span><span className={`tabular-nums font-semibold ${l.points < 0 ? "text-destructive" : "text-primary"}`}>{l.points > 0 ? "+" : ""}{l.points}</span></div>)}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex gap-2 items-end" data-testid="rewards-adjust">
          <div className="w-24"><label className="text-[11px] text-muted-foreground">Adjust (+/-)</label><Input type="number" value={adj.points} onChange={(e) => setAdj({ ...adj, points: e.target.value })} className="h-9 rounded-lg" data-testid="rewards-adjust-points" /></div>
          <div className="flex-1"><label className="text-[11px] text-muted-foreground">Reason</label><Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="h-9 rounded-lg" data-testid="rewards-adjust-reason" /></div>
          <Button variant="outline" className="h-9 rounded-lg" onClick={adjust} data-testid="rewards-adjust-save">Apply</Button>
        </div>
      )}
    </div>
  );
};
